import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Grid,
  Modal,
  Backdrop,
  Dialog,
  Collapse,
} from '@material-ui/core';
import { decode } from 'html-entities';
import CancelIcon from '@material-ui/icons/Cancel';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useState, useMemo, useCallback, useEffect } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import { useHistory } from 'react-router-dom';
import { cloneDeep, keyBy } from 'lodash';

import useAuth from 'src/hooks/useAuth';
import useCampaign from 'src/hooks/useCampaign';
import useDashboardFilters, {
  RiskFilterType,
} from 'src/hooks/useDashboardFilters';
import { useTextStyles } from 'src/hooks/useTextStyles';
import { CampaignModal } from 'src/modals/CampaignModal';
import {
  getCampaignDetail,
  getCampaignItems,
  getCampaignShowNames,
} from 'src/apis/campaign';
import { getGarmScoresPerShow } from 'src/apis/garm';
import { CampaignDetailProps, CampaignItemProps } from 'src/types/campaign';
import ButtonDropdown, { OptionProps } from 'src/components/ButtonDropdown';
import { TableWrapper, HeaderCell, Footer } from '../dashboard/garm/components';
import Spinner from 'src/components/Spinner';
import Button from 'src/components/Button';
import { useCreateDashboard, useUpdateDashboard } from '../dashboard/hooks';
import { DashboardType, Dashboard, IGarmScoreDto } from 'src/types';
import { Theme } from 'src/theme/types/createPalette';
import {
  getGarmDashboards,
  formatPodcastRatingNumber,
  getValidGarmScores,
  downloadAsCsv,
  formatPodcastRating,
} from 'src/utils';
import TextInput from 'src/components/TextInput';
import { DashboardList } from 'src/components/sidebar';
import { useDashboardData } from '../dashboard/garm/hooks';
import useDashboardCategories from 'src/hooks/useDashboardCategories';
import { ScoreFilterType, ScoreExceptionTypes } from 'src/types/filter';
import GarmArticleContent from '../content-screen/GARMArticleContent';

import { NoCampaign } from './NoCampaign';
import CampaignItemEpisodeRow from './CampaignItemEpisodeRow';
import { Row } from '../dashboard/garm/components';

const modalStyle = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  width: 350,
  bgcolor: 'background.paper',
  border: '2px solid #e0e0e0',
  borderRadius: '20px 0 0 20px',
  boxShadow: 24,
  pt: 2,
  px: 2,
  pb: 3,
};

const createNewModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 450,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      height: '100%',
      background: '#fff',
      minWidth: 600,
    },
    header: {
      borderBottom: '1px solid #EAECF0',
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
    },
    statsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      padding: '24px 16px',
    },
    statsSection: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 16,
    },
    statsSectionTitle: {
      width: '100%',
      marginBottom: 4,
    },
    reportStatsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 32,
      padding: '24px 16px',
    },
    reportSectionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 32,
    },
    reportSection: {
      border: '1px solid #EAECF0',
      borderRadius: 8,
      overflow: 'hidden',
      background: '#fff',
    },
    reportSectionHeader: {
      padding: '12px 16px',
      background: '#F9FAFB',
      borderBottom: '1px solid #EAECF0',
      fontWeight: 600,
      fontSize: 14,
      color: '#344054',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&:hover': { background: '#F2F4F7' },
    },
    reportSectionBody: {
      padding: 16,
    },
    reportDate: {
      fontSize: 14,
      color: '#667085',
      marginBottom: 24,
    },
    statBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      marginBottom: 16,
      '&:last-child': { marginBottom: 0 },
    },
    statBlockTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: '#344054',
      marginBottom: 4,
    },
    statRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      padding: '6px 0',
      fontSize: 13,
      color: '#101828',
      borderBottom: '1px solid #F2F4F7',
      '&:last-child': { borderBottom: 'none' },
    },
    statRowLabel: {
      flex: 1,
      color: '#667085',
    },
    statRowValue: {
      fontWeight: 600,
      color: '#101828',
      minWidth: 80,
      textAlign: 'right',
    },
    statRowPercent: {
      color: '#667085',
      minWidth: 70,
      textAlign: 'right',
    },
    top10Table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 13,
    },
    top10Row: {
      '& td': {
        padding: '8px 0',
        borderBottom: '1px solid #F2F4F7',
        color: '#101828',
        verticalAlign: 'top',
      },
      '&:last-child td': { borderBottom: 'none' },
    },
    top10Rank: {
      width: 28,
      color: '#667085',
      fontWeight: 600,
    },
    top10Name: {
      maxWidth: 400,
      color: '#101828',
    },
    top10Impressions: {
      textAlign: 'right',
      fontWeight: 600,
      color: '#344054',
    },
    emptyList: {
      fontSize: 13,
      color: '#98A2B3',
      fontStyle: 'italic',
      padding: '12px 0',
    },
    options: {
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      marginBottom: 24,
      gap: 8,
      flexWrap: 'wrap',
    },
    searchBar: {
      width: 320,
      border: '1px solid #D0D5DD',
      padding: '0 12px',
      borderRadius: 8,

      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    items: {
      width: '100%',
      overflow: 'scroll',
    },
    addBtn: {
      background: '#ffffff',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      height: 34,
      fontSize: 12,
      fontWeight: 600,
      color: 'rgb(52, 64, 84)',
    },
    downloadBtn: {
      background: '#ffffff',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      height: 34,
      fontSize: 12,
      fontWeight: 600,
      color: 'rgb(52, 64, 84)',
      width: 90,
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(3),
    },
    modalContent: {
      height: 'calc(100vh - 105px)',
      maxHeight: 'calc(100vh - 105px)',
      overflow: 'auto',
    },
    description: {
      padding: '2px 14px',
      borderRadius: 8,
      border: '1px solid #C3C3C7',
      width: '100%',
      height: 36,

      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
  })
);

const SEARCH_GROUPS = [
  { label: 'By Episode', value: 'episode' },
  { label: 'By Show', value: 'show' },
];

const SHOW_ONLY_SEARCH_GROUPS = [{ label: 'By Show', value: 'show' }];

const parseNum = (s: string | undefined): number =>
  s ? parseInt(String(s).replace(/,/g, ''), 10) || 0 : 0;
const parsePct = (s: string | undefined): number =>
  s ? parseFloat(String(s).replace(/%/g, '')) || 0 : 0;

const parseTop10Item = (str: string): { name: string; impressions: number } => {
  const match = str.match(/^\d+\.\s+(.+)\s+—\s+([\d,]+)$/);
  if (match) {
    return {
      name: match[1].trim(),
      impressions: parseNum(match[2]),
    };
  }
  return { name: str, impressions: 0 };
};

type CampaignReportData = {
  reportingDate: string;
  showMatched: {
    uniqueShows: number;
    uniqueShowsPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  showUnmatched: {
    uniqueShows: number;
    uniqueShowsPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeMatched: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeUnmatched: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeScored: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  episodeUnscored: {
    uniqueEpisodes: number;
    uniqueEpisodesPercent: number;
    impressions: number;
    impressionsPercent: number;
  };
  top10ShowsByImpression: { name: string; impressions: number }[];
  top10EpisodesByImpression: { name: string; impressions: number }[];
  episodeAlignment: {
    impressionsAligned: number;
    impressionsAlignedPercent: number;
    episodesAligned: number;
    episodesAlignedPercent: number;
    impressionsMisaligned: number;
    impressionsMisalignedPercent: number;
    episodesMisaligned: number;
    episodesMisalignedPercent: number;
    top10MisalignedEpisodes: { name: string; impressions: number }[];
    top10AlignedEpisodes: { name: string; impressions: number }[];
  };
  showAlignment: {
    impressionsAligned: number;
    impressionsAlignedPercent: number;
    showsAligned: number;
    showsAlignedPercent: number;
    impressionsMisaligned: number;
    impressionsMisalignedPercent: number;
    showsMisaligned: number;
    showsMisalignedPercent: number;
    top10MisalignedShows: { name: string; impressions: number }[];
    top10AlignedShows: { name: string; impressions: number }[];
  };
};

const getCampaignReportFromDetails = (
  details: CampaignDetailProps | null
): CampaignReportData | null => {
  if (!details) return null;

  const ds = details.detailedStats;
  const sm = ds?.shows_matching_stats;
  const em = ds?.episodes_matching_stats;
  const se = ds?.scored_episodes_stats;
  const overall = ds?.overall_stats;

  const showMatch = {
    uniqueShows: parseNum(sm?.matched_unique_shows),
    uniqueShowsPercent: parsePct(sm?.matched_unique_shows_pct),
    impressions: parseNum(sm?.matched_unique_show_impressions),
    impressionsPercent: parsePct(sm?.matched_unique_show_impressions_pct),
  };
  const showUnmatch = {
    uniqueShows: parseNum(sm?.unmatched_unique_shows),
    uniqueShowsPercent: parsePct(sm?.unmatched_unique_shows_pct),
    impressions: parseNum(sm?.unmatched_unique_show_impressions),
    impressionsPercent: parsePct(sm?.unmatched_unique_show_impressions_pct),
  };
  const episodeMatch = {
    uniqueEpisodes: parseNum(em?.matched_unique_episodes),
    uniqueEpisodesPercent: parsePct(em?.matched_unique_episodes_pct),
    impressions: parseNum(em?.matched_unique_episode_impressions),
    impressionsPercent: parsePct(em?.matched_unique_episode_impressions_pct),
  };
  const episodeUnmatch = {
    uniqueEpisodes: parseNum(em?.unmatched_unique_episodes),
    uniqueEpisodesPercent: parsePct(em?.unmatched_unique_episodes_pct),
    impressions: parseNum(em?.unmatched_unique_episode_impressions),
    impressionsPercent: parsePct(em?.unmatched_unique_episode_impressions_pct),
  };
  const episodeScored = {
    uniqueEpisodes: parseNum(se?.scored_unique_episodes),
    uniqueEpisodesPercent: parsePct(se?.scored_unique_episodes_pct),
    impressions: parseNum(se?.scored_unique_episode_impressions),
    impressionsPercent: parsePct(se?.scored_unique_episode_impressions_pct),
  };
  const episodeUnscored = {
    uniqueEpisodes: parseNum(se?.unscored_unique_episodes),
    uniqueEpisodesPercent: parsePct(se?.unscored_unique_episodes_pct),
    impressions: parseNum(se?.unscored_unique_episode_impressions),
    impressionsPercent: parsePct(se?.unscored_unique_episode_impressions_pct),
  };

  const top10Shows = (ds?.top10_shows_by_impression ?? []).map(parseTop10Item);
  const top10Eps = (ds?.top10_eps_by_impression ?? []).map(parseTop10Item);

  const showAligned = details.showAlignedStats ?? {
    alignedShowsCount: 0,
    alignedImpressions: 0,
    alignedShowsPercent: 0,
    misalignedShowsCount: 0,
    misalignedImpressions: 0,
    misalignedShowsPercent: 0,
    alignedImpressionsPercent: 0,
    misalignedImpressionsPercent: 0,
  };
  const episodeAligned = details.episodeAlignedStats ?? {
    alignedEpisodesCount: 0,
    alignedImpressions: 0,
    alignedEpisodesPercent: 0,
    misalignedEpisodesCount: 0,
    misalignedImpressions: 0,
    misalignedEpisodesPercent: 0,
    alignedImpressionsPercent: 0,
    misalignedImpressionsPercent: 0,
  };

  return {
    reportingDate: overall?.reporting_date ?? '',
    showMatched: showMatch,
    showUnmatched: showUnmatch,
    episodeMatched: episodeMatch,
    episodeUnmatched: episodeUnmatch,
    episodeScored,
    episodeUnscored,
    top10ShowsByImpression: top10Shows,
    top10EpisodesByImpression: top10Eps,
    episodeAlignment: {
      impressionsAligned: episodeAligned.alignedImpressions,
      impressionsAlignedPercent: episodeAligned.alignedImpressionsPercent,
      episodesAligned: episodeAligned.alignedEpisodesCount,
      episodesAlignedPercent: episodeAligned.alignedEpisodesPercent,
      impressionsMisaligned: episodeAligned.misalignedImpressions,
      impressionsMisalignedPercent: episodeAligned.misalignedImpressionsPercent,
      episodesMisaligned: episodeAligned.misalignedEpisodesCount,
      episodesMisalignedPercent: episodeAligned.misalignedEpisodesPercent,
      top10MisalignedEpisodes: [],
      top10AlignedEpisodes: top10Eps,
    },
    showAlignment: {
      impressionsAligned: showAligned.alignedImpressions,
      impressionsAlignedPercent: showAligned.alignedImpressionsPercent,
      showsAligned: showAligned.alignedShowsCount,
      showsAlignedPercent: showAligned.alignedShowsPercent,
      impressionsMisaligned: showAligned.misalignedImpressions,
      impressionsMisalignedPercent: showAligned.misalignedImpressionsPercent,
      showsMisaligned: showAligned.misalignedShowsCount,
      showsMisalignedPercent: showAligned.misalignedShowsPercent,
      top10MisalignedShows: [],
      top10AlignedShows: top10Shows,
    },
  };
};

const formatNumber = (n: number) => n.toLocaleString();

const formatCampaignReportAsText = (
  report: CampaignReportData,
  campaignName: string
): string => {
  const fmt = (n: number) => n.toLocaleString();
  const lines: string[] = [
    `${campaignName} - Campaign Report`,
    '='.repeat(60),
    '',
    `Reporting date: ${report.reportingDate || '—'}`,
    '',
    '--- Barometer Show Match ---',
    'Matched:',
    `  Barometer Matched Unique Shows: ${fmt(
      report.showMatched.uniqueShows
    )} (${report.showMatched.uniqueShowsPercent}%)`,
    `  Barometer Matched Unique Show Impressions: ${fmt(
      report.showMatched.impressions
    )} (${report.showMatched.impressionsPercent}%)`,
    'Unmatched:',
    `  Barometer Unmatched Unique Shows: ${fmt(
      report.showUnmatched.uniqueShows
    )} (${report.showUnmatched.uniqueShowsPercent}%)`,
    `  Barometer Unmatched Unique Show Impressions: ${fmt(
      report.showUnmatched.impressions
    )} (${report.showUnmatched.impressionsPercent}%)`,
    '',
    '--- Barometer Episode Match & Score ---',
    'Matched:',
    `  Barometer Matched Unique Episodes: ${fmt(
      report.episodeMatched.uniqueEpisodes
    )} (${report.episodeMatched.uniqueEpisodesPercent}%)`,
    `  Barometer Matched Unique Episode Impressions: ${fmt(
      report.episodeMatched.impressions
    )} (${report.episodeMatched.impressionsPercent}%)`,
    'Unmatched:',
    `  Barometer Unmatched Unique Episodes: ${fmt(
      report.episodeUnmatched.uniqueEpisodes
    )} (${report.episodeUnmatched.uniqueEpisodesPercent}%)`,
    `  Barometer Unmatched Unique Episode Impressions: ${fmt(
      report.episodeUnmatched.impressions
    )} (${report.episodeUnmatched.impressionsPercent}%)`,
    'Scored:',
    `  Barometer Scored Unique Episodes: ${fmt(
      report.episodeScored.uniqueEpisodes
    )} (${report.episodeScored.uniqueEpisodesPercent.toFixed(2)}%)`,
    `  Barometer Scored Unique Episode Impressions: ${fmt(
      report.episodeScored.impressions
    )} (${report.episodeScored.impressionsPercent.toFixed(2)}%)`,
    'Unscored:',
    `  Barometer Unscored Unique Episodes: ${fmt(
      report.episodeUnscored.uniqueEpisodes
    )} (${report.episodeUnscored.uniqueEpisodesPercent.toFixed(2)}%)`,
    `  Barometer Unscored Unique Episode Impressions: ${fmt(
      report.episodeUnscored.impressions
    )} (${report.episodeUnscored.impressionsPercent.toFixed(2)}%)`,
    '',
    '--- Top 10 Shows by Impression ---',
    ...report.top10ShowsByImpression.map(
      (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
    ),
    '',
    '--- Top 10 Episodes by Impression ---',
    ...report.top10EpisodesByImpression.map(
      (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
    ),
    '',
    '--- Episode Alignment Statistics ---',
    `Barometer Scored Unique Episodes Impressions Aligned: ${fmt(
      report.episodeAlignment.impressionsAligned
    )} (${report.episodeAlignment.impressionsAlignedPercent.toFixed(2)}%)`,
    `Barometer Scored Unique Episodes Aligned: ${fmt(
      report.episodeAlignment.episodesAligned
    )} (${report.episodeAlignment.episodesAlignedPercent.toFixed(2)}%)`,
    `Barometer Scored Unique Episodes Impressions Misaligned: ${fmt(
      report.episodeAlignment.impressionsMisaligned
    )} (${report.episodeAlignment.impressionsMisalignedPercent.toFixed(2)}%)`,
    `Barometer Scored Unique Episodes Misaligned: ${fmt(
      report.episodeAlignment.episodesMisaligned
    )} (${report.episodeAlignment.episodesMisalignedPercent.toFixed(2)}%)`,
    'Top 10 Misaligned Episodes by Impression:',
    ...(report.episodeAlignment.top10MisalignedEpisodes.length > 0
      ? report.episodeAlignment.top10MisalignedEpisodes.map(
          (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
        )
      : ['  — —']),
    'Top 10 Aligned Episodes by Impression:',
    ...(report.episodeAlignment.top10AlignedEpisodes.length > 0
      ? report.episodeAlignment.top10AlignedEpisodes.map(
          (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
        )
      : ['  — —']),
    '',
    '--- Show Alignment Statistics ---',
    `Barometer Matched Unique Shows Impressions Aligned: ${fmt(
      report.showAlignment.impressionsAligned
    )} (${report.showAlignment.impressionsAlignedPercent.toFixed(2)}%)`,
    `Barometer Matched Unique Shows Aligned: ${fmt(
      report.showAlignment.showsAligned
    )} (${report.showAlignment.showsAlignedPercent.toFixed(2)}%)`,
    `Barometer Matched Unique Shows Impressions Misaligned: ${fmt(
      report.showAlignment.impressionsMisaligned
    )} (${report.showAlignment.impressionsMisalignedPercent.toFixed(2)}%)`,
    `Barometer Matched Unique Shows Misaligned: ${fmt(
      report.showAlignment.showsMisaligned
    )} (${report.showAlignment.showsMisalignedPercent.toFixed(2)}%)`,
    'Top 10 Misaligned Shows by Impression:',
    ...(report.showAlignment.top10MisalignedShows.length > 0
      ? report.showAlignment.top10MisalignedShows.map(
          (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
        )
      : ['  — —']),
    'Top 10 Aligned Shows by Impression:',
    ...(report.showAlignment.top10AlignedShows.length > 0
      ? report.showAlignment.top10AlignedShows.map(
          (item, i) => `  ${i + 1}. ${item.name} — ${fmt(item.impressions)}`
        )
      : ['  —']),
  ];
  return lines.join('\n');
};

const downloadReportAsTxt = (content: string, fileName: string) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${fileName.replace(/[^a-zA-Z0-9-_]/g, '_')}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export default function Campaigns() {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { organization } = useAuth();
  const { selectedCampaignId, openCreateModal, setOpenCreateModal } =
    useCampaign();
  const history = useHistory();
  const { fetchAllShowNamesDashboard } = useDashboardData();
  const [showsDownloading, setShowsDownloading] = useState(false);
  const [currentCampaign, setCurrentCampaign] =
    useState<Nullable<IGarmScoreDto>>(null);
  const [currentShow, setCurrentShow] = useState<Nullable<string>>(null);
  const [currentEpisode, setCurrentEpisode] = useState<Nullable<string>>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [itemsLoading, setItemsLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<CampaignDetailProps | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [group, setGroup] = useState<OptionProps>(SEARCH_GROUPS[0]);

  // Show & Episode Alignment: both show and episode options when episodeAlignedStats exists. Other cases: show only.
  const isShowAndEpisodeAlignment = details?.episodeAlignedStats !== undefined;
  const availableGroups = isShowAndEpisodeAlignment
    ? SEARCH_GROUPS
    : SHOW_ONLY_SEARCH_GROUPS;

  // Force group to 'show' when not Show & Episode Alignment
  useEffect(() => {
    if (!isShowAndEpisodeAlignment && group.value === 'episode') {
      setGroup(SHOW_ONLY_SEARCH_GROUPS[0]);
    }
  }, [isShowAndEpisodeAlignment, group.value]);
  const { garmScoreColumns, selectDashboard, customFilters } =
    useDashboardFilters();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<CampaignItemProps[]>([]);
  const [showItems, setShowItems] = useState<CampaignItemProps[]>([]);
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [createNewModal, setCreateNewModal] = useState<boolean>(false);
  const [newDashboardName, setNewDashboardName] = useState<string>('');
  const [brandStandardFilterOption, setBrandStandardFilterOption] =
    useState<OptionProps | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    showMatch: true,
    episodeMatch: true,
    top10Shows: true,
    top10Episodes: true,
    episodeAlignment: true,
    showAlignment: true,
  });

  const toggleSection = (key: string) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const { onCreateDashboard } = useCreateDashboard();
  const { onUpdateDashboard } = useUpdateDashboard();
  const { filterMapInit } = useDashboardCategories();
  const firstLoading = details?.id !== selectedCampaignId;

  const campaignReport = useMemo(
    () => getCampaignReportFromDetails(details),
    [details]
  );

  const riskFilters = cloneDeep(filterMapInit);

  const enabledFilters = useMemo(() => {
    const temp: RiskFilterType[] = [];

    Object.keys(riskFilters).forEach((key) => {
      if (
        riskFilters[key].enabled ||
        riskFilters[key].filterType === 'audience'
      ) {
        temp.push(riskFilters[key]);
      }
    });
    return temp;
  }, [riskFilters]);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchCampaignDetails = useCallback(
    async (
      id: string,
      brandStandardFilterOption: OptionProps | null,
      enableLoading?: boolean
    ) => {
      if (enableLoading) {
        setLoading(true);
      }

      try {
        await getCampaignShowNames(id);
        const selectedCustomFilter = brandStandardFilterOption?.value
          ? customFilters.filter(
              (item) => item.id === brandStandardFilterOption?.value
            )[0]
          : null;
        const response = await getCampaignDetail(id);
        setDetails(response);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (selectedCampaignId) {
      fetchCampaignDetails(
        selectedCampaignId,
        brandStandardFilterOption,
        firstLoading
      );
    }
  }, [
    selectedCampaignId,
    brandStandardFilterOption,
    firstLoading,
    fetchCampaignDetails,
  ]);

  const fetchCampaignItems = useCallback(
    async (
      id: string,
      page: number,
      pageSize: number,
      groupBy: string,
      search: string,
      brandStandardFilterOption: OptionProps | null
    ) => {
      setItemsLoading(true);
      try {
        const selectedCustomFilter = brandStandardFilterOption?.value
          ? customFilters.filter(
              (item) => item.id === brandStandardFilterOption?.value
            )[0]
          : null;
        let garmExceptions: any = {};
        if (selectedCustomFilter) {
          enabledFilters.forEach((filter) => {
            const customFilter =
              selectedCustomFilter.data.scoreFilters[filter.title];
            if (customFilter) {
              garmExceptions[filter.dashboardFilterKey] = {
                default: getValidGarmScores(customFilter.score),
              };
              if (customFilter.content.length) {
                const content: any = {};
                customFilter.content.forEach((item: ScoreFilterType) => {
                  if (item.exceptions?.length) {
                    content[
                      item.type === ScoreExceptionTypes.Genre
                        ? 'genre'
                        : 'garmReasoning'
                    ] = item.exceptions;
                  }
                });
                garmExceptions[filter.dashboardFilterKey] = {
                  ...garmExceptions[filter.dashboardFilterKey],
                  ...content,
                };
              }
            }
          });
        } else {
          garmExceptions = undefined;
        }
        const response = await getCampaignItems(
          id,
          {
            page,
            pageSize,
            groupBy,
            search,
          },
          garmExceptions
        );
        if (groupBy === 'episode') {
          setItems(response.items);
        } else {
          setShowItems(response.items);
        }
        setTotalItemsCount(response.count);
        setItemsLoading(false);
      } catch (error) {
        setItemsLoading(false);
      }
    },
    [selectedCampaignId, customFilters]
  );

  useEffect(() => {
    if (selectedCampaignId) {
      fetchCampaignItems(
        selectedCampaignId,
        page,
        pageSize,
        group.value,
        searchText,
        brandStandardFilterOption
      );
    }
  }, [
    selectedCampaignId,
    page,
    pageSize,
    group,
    searchText,
    brandStandardFilterOption,
    fetchCampaignItems,
  ]);

  const garmDashboards = useMemo(
    () => (organization ? getGarmDashboards(organization) : []),
    [organization]
  );

  const columns = useMemo(() => {
    if (group.value === 'episode' && isShowAndEpisodeAlignment) {
      return [
        'Episode',
        'Podcast Title',
        'Aligned',
        'Impressions',
        ...garmScoreColumns,
      ];
    }
    return ['Podcast Title', 'Aligned', 'Impressions', ...garmScoreColumns];
  }, [group, garmScoreColumns, isShowAndEpisodeAlignment]);

  const handleNewDashboard = async (name: string) => {
    if (!selectedCampaignId || !name) {
      return;
    }
    const showNames = await getCampaignShowNames(selectedCampaignId);

    const garmFilters = keyBy(
      Object.values(riskFilters).map((riskFilter) => ({
        dashboardFilterKey: riskFilter.dashboardFilterKey,
        enabled: riskFilter.dashboardFilterKey === 'audience' ? false : true,
        value: riskFilter.dashboardFilterKey === 'audience' ? [0, 5] : [0, 3],
      })),
      'dashboardFilterKey'
    );

    const dashboard = await onCreateDashboard({
      type: DashboardType.GARM,
      keywordsList: showNames,
      garmFilters: {
        ...garmFilters,
        publishersList: [],
        iabCategories: [],
        genresList: [],
      },
      exceptionFilterId: null,
      name,
    });

    if (dashboard) {
      selectDashboard(dashboard);
      history.push('/dashboard');
    }
  };

  const handleSelectDashboard = async (selectedDashboard: Dashboard) => {
    if (selectedDashboard && selectedCampaignId) {
      setOpenModal(false);
      const showNames = await getCampaignShowNames(selectedCampaignId);
      const existingNames = await fetchAllShowNamesDashboard(selectedDashboard);

      const garmFiltersData = {
        name: selectedDashboard.name,
        type: DashboardType.GARM,
        keywordsList: [...existingNames, ...showNames],
        garmFilters: selectedDashboard.garmFilters,
        exceptionFilterId: selectedDashboard.exceptionFilterId || null,
      };
      await onUpdateDashboard(selectedDashboard.id, garmFiltersData);
      selectDashboard({
        ...selectedDashboard,
        keywordsList: [...existingNames, ...showNames],
      });
      history.push('/dashboard');
    }
  };

  const handleDownload = async () => {
    if (!selectedCampaignId) {
      return;
    }
    setShowsDownloading(true);

    const selectedCustomFilter = brandStandardFilterOption?.value
      ? customFilters.filter(
          (item) => item.id === brandStandardFilterOption?.value
        )[0]
      : null;
    let garmExceptions: any = {};
    if (selectedCustomFilter) {
      enabledFilters.forEach((filter) => {
        const customFilter =
          selectedCustomFilter.data.scoreFilters[filter.title];
        if (customFilter) {
          garmExceptions[filter.dashboardFilterKey] = {
            default: getValidGarmScores(customFilter.score),
          };
          if (customFilter.content.length) {
            const content: any = {};
            customFilter.content.forEach((item: ScoreFilterType) => {
              if (item.exceptions?.length) {
                content[
                  item.type === ScoreExceptionTypes.Genre
                    ? 'genre'
                    : 'garmReasoning'
                ] = item.exceptions;
              }
            });
            garmExceptions[filter.dashboardFilterKey] = {
              ...garmExceptions[filter.dashboardFilterKey],
              ...content,
            };
          }
        }
      });
    } else {
      garmExceptions = undefined;
    }
    const { items } = await getCampaignItems(
      selectedCampaignId,
      {
        page: 0,
        pageSize: 1000000,
        groupBy: group.value,
        search: searchText,
      },
      garmExceptions
    );

    await downloadAsCsv({
      data: formatDownloadData(items),
      headers: [...columns],
      fileName: `${details?.name} Campaign ${group.label} Download`,
    });

    handleDownloadReport();

    setShowsDownloading(false);
  };

  const handleDownloadReport = () => {
    if (!campaignReport || !details?.name) return;
    const text = formatCampaignReportAsText(campaignReport, details.name);
    downloadReportAsTxt(text, `${details.name} Campaign Report`);
  };

  const formatDownloadData = (data: CampaignItemProps[]) => {
    if (group.value === 'episode') {
      return data
        .map((each) =>
          [
            each.episode['Episode Title'],
            each.showIdentifier,
            each.isAligned ? 'Aligned' : '',
            each.impressionCount,
            each.episode['GARM 1 Risk'],
            each.episode['GARM 7 Risk'],
            each.episode['GARM 11 Risk'],
            each.episode['GARM 8 Risk'],
            each.episode['GARM 2 Risk'],
            each.episode['GARM 5 & 9 Risk'],
            each.episode['GARM 10 Risk'],
            each.episode['GARM 4 Risk'],
            each.episode['GARM 3 Risk'],
            each.episode['GARM 6 Risk'],
            each.episode['GAMBLING Risk'],
            each.episode['OCCULT Risk'],
            each.episode['NATURAL DISASTER Risk'],
            each.episode['GENERAL ELECTIONS Risk'],
          ]
            .map((value) => {
              if (!value) return value;
              let result = String(value).replace(/"/g, '""');
              if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
              return result;
            })
            .join(',')
        )
        .join('\n');
    }
    return data
      .map((each) =>
        [
          each.show['show'],
          each.isAligned ? 'Aligned' : '',
          each.impressionCount,
          each.show?.riskHistories?.current?.garm1Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm7Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm11Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm8Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm2Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm5Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm10Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm4Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm3Risk?.medianRisk,
          each.show?.riskHistories?.current?.garm6Risk?.medianRisk,
          each.show?.riskHistories?.current?.gamblingRisk?.medianRisk,
          each.show?.riskHistories?.current?.occultRisk?.medianRisk,
          each.show?.riskHistories?.current?.naturalDisasterRisk?.medianRisk,
          each.show?.riskHistories?.current?.generalElectionsRisk?.medianRisk,
        ]
          .map((value) => {
            if (!value) return value;
            let result = String(value).replace(/"/g, '""');
            if (result.search(/("|,|\n)/g) >= 0) result = '"' + result + '"';
            return result;
          })
          .join(',')
      )
      .join('\n');
  };

  const fetchGarmScoresPerShow = async (
    showName: string,
    episodeName: string
  ) => {
    try {
      setLoading(true);
      const data = await getGarmScoresPerShow(showName, {});
      const episodes = data.data.map((item: any) => {
        const newItem = {
          ...item,
          Listeners: formatPodcastRating(item.Listeners),
        };

        return newItem;
      });
      const episode = episodes.find(
        (item) => item['Episode Title'] === episodeName
      );

      if (episode) {
        setCurrentCampaign(episode);
        setCurrentShow(showName);
      }
    } catch (error) {
      setCurrentCampaign(null);
      setCurrentShow(null);
    } finally {
      setLoading(false);
    }
  };

  if (!organization) {
    return null;
  }

  return (
    <>
      {selectedCampaignId ? (
        <>
          {loading || !details ? (
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              className={classes.container}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box className={classes.container}>
              <Box className={classes.header}>
                <Typography
                  className={textClasses.xxlBold}
                  style={{ color: '#344054' }}
                >
                  {details.name}
                </Typography>
                <Typography
                  className={textClasses.baseRegular}
                  style={{ color: '#98A2B3', marginTop: 8 }}
                >
                  {details.description}
                </Typography>
              </Box>
              <Box className={classes.reportStatsContainer}>
                <Typography className={classes.reportDate}>
                  Reporting date: {campaignReport?.reportingDate ?? '—'}
                </Typography>

                <Box className={classes.reportSectionsGrid}>
                  {/* Show Matched / Unmatched */}
                  {group.value === 'show' && (
                    <Box className={classes.reportSection}>
                      <Box
                        className={classes.reportSectionHeader}
                        onClick={() => toggleSection('showMatch')}
                      >
                        Barometer Show Match
                        {expandedSections.showMatch ? (
                          <ExpandLessIcon fontSize='small' />
                        ) : (
                          <ExpandMoreIcon fontSize='small' />
                        )}
                      </Box>
                      <Collapse in={expandedSections.showMatch}>
                        <Box className={classes.reportSectionBody}>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Matched
                            </Typography>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Shows
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showMatched.uniqueShows ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.showMatched
                                  .uniqueShowsPercent ?? 0}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Show Impressions
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showMatched.impressions ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.showMatched
                                  .impressionsPercent ?? 0}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Unmatched
                            </Typography>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Unmatched Unique Shows
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showUnmatched.uniqueShows ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.showUnmatched
                                  .uniqueShowsPercent ?? 0}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Unmatched Unique Show Impressions
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showUnmatched.impressions ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.showUnmatched
                                  .impressionsPercent ?? 0}
                                %
                              </span>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* Episode Matched / Unmatched / Scored / Unscored */}
                  {group.value === 'episode' && (
                    <Box className={classes.reportSection}>
                      <Box
                        className={classes.reportSectionHeader}
                        onClick={() => toggleSection('episodeMatch')}
                      >
                        Barometer Episode Match & Score
                        {expandedSections.episodeMatch ? (
                          <ExpandLessIcon fontSize='small' />
                        ) : (
                          <ExpandMoreIcon fontSize='small' />
                        )}
                      </Box>
                      <Collapse in={expandedSections.episodeMatch}>
                        <Box className={classes.reportSectionBody}>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Matched
                            </Typography>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Episodes
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeMatched
                                    .uniqueEpisodes ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.episodeMatched
                                  .uniqueEpisodesPercent ?? 0}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Episode Impressions
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeMatched.impressions ??
                                    0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.episodeMatched
                                  .impressionsPercent ?? 0}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Unmatched
                            </Typography>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Unmatched Unique Episodes
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeUnmatched
                                    .uniqueEpisodes ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.episodeUnmatched
                                  .uniqueEpisodesPercent ?? 0}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Unmatched Unique Episode Impressions
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeUnmatched
                                    .impressions ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {campaignReport?.episodeUnmatched
                                  .impressionsPercent ?? 0}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Scored
                            </Typography>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Scored Unique Episodes
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeScored
                                    .uniqueEpisodes ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeScored
                                    .uniqueEpisodesPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Scored Unique Episode Impressions
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeScored.impressions ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeScored
                                    .impressionsPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Unscored
                            </Typography>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Unscored Unique Episodes
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeUnscored
                                    .uniqueEpisodes ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeUnscored
                                    .uniqueEpisodesPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Unscored Unique Episode Impressions
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeUnscored.impressions ??
                                    0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeUnscored
                                    .impressionsPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* Top 10 Shows by Impression */}
                  {group.value === 'show' && (
                    <Box className={classes.reportSection}>
                      <Box
                        className={classes.reportSectionHeader}
                        onClick={() => toggleSection('top10Shows')}
                      >
                        Top 10 Shows by Impression
                        {expandedSections.top10Shows ? (
                          <ExpandLessIcon fontSize='small' />
                        ) : (
                          <ExpandMoreIcon fontSize='small' />
                        )}
                      </Box>
                      <Collapse in={expandedSections.top10Shows}>
                        <Box className={classes.reportSectionBody}>
                          <table className={classes.top10Table}>
                            <tbody>
                              {(
                                campaignReport?.top10ShowsByImpression ?? []
                              ).map((item, idx) => (
                                <tr key={idx} className={classes.top10Row}>
                                  <td className={classes.top10Rank}>
                                    {idx + 1}.
                                  </td>
                                  <td className={classes.top10Name}>
                                    {item.name}
                                  </td>
                                  <td className={classes.top10Impressions}>
                                    {formatNumber(item.impressions)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* Top 10 Episodes by Impression */}
                  {group.value === 'episode' && (
                    <Box className={classes.reportSection}>
                      <Box
                        className={classes.reportSectionHeader}
                        onClick={() => toggleSection('top10Episodes')}
                      >
                        Top 10 Episodes by Impression
                        {expandedSections.top10Episodes ? (
                          <ExpandLessIcon fontSize='small' />
                        ) : (
                          <ExpandMoreIcon fontSize='small' />
                        )}
                      </Box>
                      <Collapse in={expandedSections.top10Episodes}>
                        <Box className={classes.reportSectionBody}>
                          <table className={classes.top10Table}>
                            <tbody>
                              {(
                                campaignReport?.top10EpisodesByImpression ?? []
                              ).map((item, idx) => (
                                <tr key={idx} className={classes.top10Row}>
                                  <td className={classes.top10Rank}>
                                    {idx + 1}.
                                  </td>
                                  <td className={classes.top10Name}>
                                    {item.name}
                                  </td>
                                  <td className={classes.top10Impressions}>
                                    {formatNumber(item.impressions)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* Episode Alignment Statistics */}
                  {group.value === 'episode' && (
                    <Box className={classes.reportSection}>
                      <Box
                        className={classes.reportSectionHeader}
                        onClick={() => toggleSection('episodeAlignment')}
                      >
                        Episode Alignment Statistics
                        {expandedSections.episodeAlignment ? (
                          <ExpandLessIcon fontSize='small' />
                        ) : (
                          <ExpandMoreIcon fontSize='small' />
                        )}
                      </Box>
                      <Collapse in={expandedSections.episodeAlignment}>
                        <Box className={classes.reportSectionBody}>
                          <Box className={classes.statBlock}>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Scored Unique Episodes Impressions
                                Aligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeAlignment
                                    .impressionsAligned ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeAlignment
                                    .impressionsAlignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Scored Unique Episodes Aligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeAlignment
                                    .episodesAligned ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeAlignment
                                    .episodesAlignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Scored Unique Episodes Impressions
                                Misaligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeAlignment
                                    .impressionsMisaligned ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeAlignment
                                    .impressionsMisalignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Scored Unique Episodes Misaligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.episodeAlignment
                                    .episodesMisaligned ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.episodeAlignment
                                    .episodesMisalignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Top 10 Misaligned Episodes by Impression
                            </Typography>
                            {(campaignReport?.episodeAlignment
                              .top10MisalignedEpisodes?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .top10MisalignedEpisodes ?? []
                                  ).map(
                                    (
                                      item: {
                                        name: string;
                                        impressions: number;
                                      },
                                      idx: number
                                    ) => (
                                      <tr
                                        key={idx}
                                        className={classes.top10Row}
                                      >
                                        <td className={classes.top10Rank}>
                                          {idx + 1}.
                                        </td>
                                        <td className={classes.top10Name}>
                                          {item.name}
                                        </td>
                                        <td
                                          className={classes.top10Impressions}
                                        >
                                          {formatNumber(item.impressions)}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Top 10 Aligned Episodes by Impression
                            </Typography>
                            {(campaignReport?.episodeAlignment
                              .top10AlignedEpisodes?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.episodeAlignment
                                      .top10AlignedEpisodes ?? []
                                  ).map(
                                    (
                                      item: {
                                        name: string;
                                        impressions: number;
                                      },
                                      idx: number
                                    ) => (
                                      <tr
                                        key={idx}
                                        className={classes.top10Row}
                                      >
                                        <td className={classes.top10Rank}>
                                          {idx + 1}.
                                        </td>
                                        <td className={classes.top10Name}>
                                          {item.name}
                                        </td>
                                        <td
                                          className={classes.top10Impressions}
                                        >
                                          {formatNumber(item.impressions)}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* Show Alignment Statistics */}
                  {group.value === 'show' && (
                    <Box className={classes.reportSection}>
                      <Box
                        className={classes.reportSectionHeader}
                        onClick={() => toggleSection('showAlignment')}
                      >
                        Show Alignment Statistics
                        {expandedSections.showAlignment ? (
                          <ExpandLessIcon fontSize='small' />
                        ) : (
                          <ExpandMoreIcon fontSize='small' />
                        )}
                      </Box>
                      <Collapse in={expandedSections.showAlignment}>
                        <Box className={classes.reportSectionBody}>
                          <Box className={classes.statBlock}>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Shows Impressions
                                Aligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showAlignment
                                    .impressionsAligned ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.showAlignment
                                    .impressionsAlignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Shows Aligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showAlignment.showsAligned ??
                                    0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.showAlignment
                                    .showsAlignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Shows Impressions
                                Misaligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showAlignment
                                    .impressionsMisaligned ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.showAlignment
                                    .impressionsMisalignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                            <Box className={classes.statRow}>
                              <span className={classes.statRowLabel}>
                                Barometer Matched Unique Shows Misaligned
                              </span>
                              <span className={classes.statRowValue}>
                                {formatNumber(
                                  campaignReport?.showAlignment
                                    .showsMisaligned ?? 0
                                )}
                              </span>
                              <span className={classes.statRowPercent}>
                                {(
                                  campaignReport?.showAlignment
                                    .showsMisalignedPercent ?? 0
                                ).toFixed(2)}
                                %
                              </span>
                            </Box>
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Top 10 Misaligned Shows by Impression
                            </Typography>
                            {(campaignReport?.showAlignment.top10MisalignedShows
                              ?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.showAlignment
                                      .top10MisalignedShows ?? []
                                  ).map(
                                    (
                                      item: {
                                        name: string;
                                        impressions: number;
                                      },
                                      idx: number
                                    ) => (
                                      <tr
                                        key={idx}
                                        className={classes.top10Row}
                                      >
                                        <td className={classes.top10Rank}>
                                          {idx + 1}.
                                        </td>
                                        <td className={classes.top10Name}>
                                          {item.name}
                                        </td>
                                        <td
                                          className={classes.top10Impressions}
                                        >
                                          {formatNumber(item.impressions)}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                — —
                              </Typography>
                            )}
                          </Box>
                          <Box className={classes.statBlock}>
                            <Typography className={classes.statBlockTitle}>
                              Top 10 Aligned Shows by Impression
                            </Typography>
                            {(campaignReport?.showAlignment.top10AlignedShows
                              ?.length ?? 0) > 0 ? (
                              <table className={classes.top10Table}>
                                <tbody>
                                  {(
                                    campaignReport?.showAlignment
                                      .top10AlignedShows ?? []
                                  ).map((item, idx) => (
                                    <tr key={idx} className={classes.top10Row}>
                                      <td className={classes.top10Rank}>
                                        {idx + 1}.
                                      </td>
                                      <td className={classes.top10Name}>
                                        {item.name}
                                      </td>
                                      <td className={classes.top10Impressions}>
                                        {formatNumber(item.impressions)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <Typography className={classes.emptyList}>
                                —
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box className={classes.options}>
                <TextField
                  InputLabelProps={{
                    shrink: true,
                    style: { fontWeight: 'normal' },
                  }}
                  InputProps={{
                    placeholder: 'Search campaigns...',
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon
                          htmlColor='#858591'
                          style={{ fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  className={classes.searchBar}
                />
                <ButtonDropdown
                  id='search-group'
                  selectedOption={group}
                  options={availableGroups}
                  handleSelect={setGroup}
                />
                <ButtonDropdown
                  id='campaign-brand-filter'
                  defaultLabel='Select brand standard filter'
                  selectedOption={brandStandardFilterOption}
                  options={[
                    { label: 'None', value: 'No brand standard filter' },
                    ...customFilters.map((filter) => ({
                      label: filter.name,
                      value: filter.id,
                    })),
                  ]}
                  handleSelect={(option) => {
                    if (option.value === 'No brand standard filter') {
                      setBrandStandardFilterOption(null);
                    } else {
                      setBrandStandardFilterOption(option);
                    }
                  }}
                />
                <Button
                  className={classes.addBtn}
                  onClick={() => {
                    setOpenModal(true);
                  }}
                >
                  + Add To
                </Button>
                <Button
                  className={classes.addBtn}
                  onClick={() => {
                    setCreateNewModal(true);
                  }}
                >
                  + Create New
                </Button>
                <Button
                  disabed={showsDownloading}
                  className={classes.downloadBtn}
                  onClick={handleDownload}
                >
                  {showsDownloading ? <Spinner size={12} /> : 'Download'}
                </Button>
              </Box>
              <Box className={classes.items}>
                <TableContainer
                  component={Paper}
                  style={{
                    overflowX: 'scroll',
                    boxShadow: '0px 0px 0px 1px #E0E0E0',
                    borderRadius: 4,
                    flex: 1,
                    flexGrow: 1,
                  }}
                >
                  <TableWrapper
                    stickyHeader
                    style={{
                      borderRadius: 4,
                      height: '100%',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <TableHead
                      style={{
                        position: 'sticky',
                        top: -1,
                        zIndex: 2,
                      }}
                    >
                      <TableRow>
                        {columns.map((column: string, index: number) => (
                          <HeaderCell
                            key={column}
                            sortDirection={false}
                            style={
                              index === 0
                                ? {
                                    minWidth: 300,
                                    padding: '4px 10px',
                                    borderRadius: 0,
                                    border: '1px solid #F0F0F1',
                                    borderBottom: 'none',
                                    background: '#F8F8F8',
                                  }
                                : {
                                    minWidth: [
                                      'Aligned',
                                      'Impressions',
                                    ].includes(column)
                                      ? 120
                                      : 220,
                                    padding: '4px 10px',
                                    borderRadius: 0,
                                    border: '1px solid #F0F0F1',
                                    borderBottom: 'none',
                                    background: '#F8F8F8',
                                  }
                            }
                          >
                            <span>{column}</span>
                          </HeaderCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    {itemsLoading ? (
                      <TableRow style={{ height: '100%', border: 'none' }}>
                        <TableCell colSpan={columns.length}>
                          <Spinner />
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableBody>
                        {group.value === 'episode'
                          ? items.map((item, index) => (
                              <CampaignItemEpisodeRow
                                key={`${item.id}-${index}`}
                                columns={columns}
                                garmScoreColumns={garmScoreColumns}
                                data={item}
                                onClick={(showName, episodeName) => {
                                  fetchGarmScoresPerShow(showName, episodeName);
                                }}
                              />
                            ))
                          : showItems.map((data, index) => {
                              const item = data.show;
                              const newItem = {
                                ...item,
                                iabCategories:
                                  item.iabCategories &&
                                  Object.keys(item.iabCategories)
                                    .map((key) => ({
                                      count: item.iabCategories[key],
                                      name: key,
                                    }))
                                    .sort((a: any, b: any) => b.count - a.count)
                                    .slice(0, 3),
                                show: decode(item.show),
                                showOnDB: item.show,
                                listenerCount: formatPodcastRatingNumber(
                                  item.rating
                                ),
                              };
                              if (
                                item.riskHistories &&
                                item.riskHistories.current
                              ) {
                                Object.keys(item.riskHistories.current).forEach(
                                  (key: string) => {
                                    const historyValues =
                                      item.riskHistories.current[key];
                                    if (historyValues) {
                                      if (key === 'electionsPercentage') {
                                        newItem[key] = {
                                          value: historyValues.medianRiskValue,
                                          up: historyValues.episodeCountHigherThanAverage,
                                          down: historyValues.episodeCountLowerThanAverage,
                                        };
                                      } else {
                                        const score =
                                          historyValues.medianRiskValue;
                                        const oldScore =
                                          item.riskHistories.prev &&
                                          item.riskHistories.prev[key]
                                            ? item.riskHistories.prev[key]
                                                .medianRiskValue
                                            : undefined;
                                        const updatedShow =
                                          score &&
                                          oldScore &&
                                          score !== oldScore;

                                        newItem[key] = {
                                          ...historyValues,
                                          score,
                                          oldScore,
                                          updatedShow,
                                        };
                                      }
                                    }
                                  }
                                );
                              }
                              return (
                                <Row
                                  key={`${item.showId}-${index}`}
                                  name={item.show}
                                  columns={columns}
                                  garmScoreColumns={garmScoreColumns}
                                  data={{
                                    ...newItem,
                                    impressionCount: data.impressionCount,
                                    isAligned: data.isAligned,
                                  }}
                                  isHost={false}
                                  hostData={{}}
                                  onClick={() => {}}
                                />
                              );
                            })}
                        {/* This final row exists so that the other table rows don't expand in height (and look distorted) if there aren't enough rows to fill the full height of the table */}
                        <TableRow style={{ height: '100%', border: 'none' }}>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    )}
                    <Footer
                      style={{
                        position: 'sticky',
                        bottom: -1,
                        zIndex: 1,
                      }}
                    >
                      <TableRow>
                        <TableCell
                          colSpan={1000}
                          style={{
                            padding: '6px 12px',
                            boxShadow: 'inset 0 7px 2px -7px rgba(0,0,0,0.4)',
                          }}
                        >
                          <Grid
                            container
                            justifyContent='space-between'
                            alignItems='center'
                            style={{
                              position: 'sticky',
                              top: '50%',
                              left: 20,
                              maxWidth: 500,
                            }}
                          >
                            {/* @ts-ignore */}
                            <TablePagination
                              rowsPerPageOptions={[10, 20, 50, 100]}
                              SelectProps={{
                                inputProps: { 'aria-label': 'rows per page' },
                                native: true,
                              }}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              count={totalItemsCount}
                              page={page}
                              rowsPerPage={pageSize}
                              style={{
                                paddingTop: 0,
                                paddingBottom: 0,
                                fontSize: 12,
                              }}
                              nextIconButtonProps={{
                                size: 'small',
                              }}
                              backIconButtonProps={{
                                size: 'small',
                              }}
                            />
                          </Grid>
                        </TableCell>
                      </TableRow>
                    </Footer>
                  </TableWrapper>
                </TableContainer>
              </Box>
            </Box>
          )}
        </>
      ) : (
        <NoCampaign onNewCampaign={() => setOpenCreateModal(true)} />
      )}

      <CampaignModal
        open={openCreateModal}
        closeModal={() => setOpenCreateModal(false)}
      />
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby='report-dashboard-modal-title'
        aria-describedby='report-dashboard-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...modalStyle }}>
          <CancelIcon
            className={classes.closeIcon}
            onClick={() => setOpenModal(false)}
          />
          <DashboardList
            title='Select Dashboard'
            dashboards={garmDashboards}
            onSelectDashboard={handleSelectDashboard}
            onChangeFavorite={() => {}}
            selectedDashboardId={null}
            isDashboardSelection
          />
        </Box>
      </Modal>
      <Modal
        open={createNewModal}
        onClose={() => setCreateNewModal(false)}
        aria-labelledby='report-graph-modal-title'
        aria-describedby='report-graph-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...createNewModalStyle }}>
          <CancelIcon
            className={classes.closeIcon}
            onClick={() => setCreateNewModal(false)}
          />
          <Typography variant='body1' style={{ fontSize: 14 }}>
            Dashboard Name
          </Typography>
          <TextInput
            default='Enter name'
            value={newDashboardName}
            onChange={(name) => setNewDashboardName(name)}
            className={classes.description}
          />
          <Box
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              title='Cancel'
              variant='outlined'
              height={36}
              onClick={() => setCreateNewModal(false)}
            />
            <Button
              disabled={!newDashboardName}
              title='Create'
              height={36}
              onClick={() => {
                handleNewDashboard(newDashboardName);
              }}
            />
          </Box>
        </Box>
      </Modal>
      <Dialog open={!!currentCampaign} fullScreen>
        <GarmArticleContent
          episode={currentCampaign}
          show={currentShow || ''}
          showGenres={[]}
          onClose={() => {
            setCurrentCampaign(null);
            setCurrentShow(null);
          }}
        />
      </Dialog>
    </>
  );
}
